import { ComponentPropsWithoutRef, forwardRef, useMemo } from 'react';
import { ArrowDownLeft, ArrowUpRight, Minus } from 'lucide-react';

import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

export interface ChangeProps
  extends Omit<ComponentPropsWithoutRef<'p'>, 'children'> {
  from: number;
  to: number;
}

/**
 * Props and ref are forwarded to the root element so this can be the child of
 * a `Tooltip`, which clones its trigger.
 */
export const Change = forwardRef<HTMLParagraphElement, ChangeProps>(
  function Change({ from, to, className, ...props }, ref) {
    const status =
      from === to ? 'neutral' : to > from ? 'positive' : 'negative';

    const difference = Math.abs(to - from);
    const change = from === 0 ? 0 : (difference / from) * 100;

    const value = useMemo(() => {
      if (change === 0) return '0%';

      const decimals = change < 10 ? 1 : 0;

      // Ensure the value is not 0% since there is _a_ change
      let value = change.toFixed(decimals).replace(/\.00?$/, '');
      if (value === '0') {
        value = change.toFixed(decimals + 1).replace(/\.00?$/, '');
        if (value === '0') {
          value = '0.01';
        }
      }

      return `${value}%`;
    }, [change]);

    const label = useMemo(() => {
      if (status === 'neutral') {
        return 'No change';
      } else if (status === 'positive') {
        return `Increased by ${value}`;
      } else {
        return `Decreased by ${value}`;
      }
    }, [status, value]);

    return (
      <p
        {...props}
        ref={ref}
        className={cn(css.change, className)}
        data-status={status}
        aria-label={label}
      >
        <span className={css.changeValue}>{value}</span>
        <span className={css.changeIcon} aria-hidden>
          {status === 'neutral' ? (
            <Minus />
          ) : status === 'positive' ? (
            <ArrowUpRight />
          ) : (
            <ArrowDownLeft />
          )}
        </span>
      </p>
    );
  }
);
